import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../core/contexts/AuthContext';
import { getDefaultRedirectPath } from '../core/utils/authHelpers';
import Alert from '../shared/components/ui/Alert';

/** Password strength requirements (must match server-side schema) */
function checkPassword(pw) {
  const errors = [];
  if (pw.length < 8)           errors.push('Mindst 8 tegn');
  if (!/[A-Z]/.test(pw))       errors.push('Stort bogstav');
  if (!/[a-z]/.test(pw))       errors.push('Lille bogstav');
  if (!/[0-9]/.test(pw))       errors.push('Et tal');
  if (!/[^A-Za-z0-9]/.test(pw)) errors.push('Specialtegn');
  return errors;
}

export default function SetPasswordPage() {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { rehydratePlayer, user, userRoles } = useAuth();

  const [memberNumber, setMemberNumber] = useState(searchParams.get('mn') || '');
  const [memberInfo, setMemberInfo]     = useState(null); // { name, hasEmail }
  const [email, setEmail]               = useState('');
  const [password, setPassword]         = useState('');
  const [confirm, setConfirm]           = useState('');
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [pwErrors, setPwErrors]         = useState([]);

  // Redirect if already logged in
  useEffect(() => {
    if (user && userRoles.length > 0) {
      navigate(getDefaultRedirectPath(userRoles), { replace: true });
    }
  }, [user, userRoles, navigate]);

  // Pre-fetch member info when member number is entered
  useEffect(() => {
    if (!memberNumber.trim()) { setMemberInfo(null); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch('/api/player-auth-ext/check-member', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ memberNumber: memberNumber.trim() }),
        });
        const data = await res.json();
        if (res.ok && data.found) {
          if (data.hasAccount) {
            setError(t('setPassword.error.accountExists'));
            setMemberInfo(null);
          } else {
            setMemberInfo(data);
            setError('');
          }
        } else if (res.status === 404) {
          setMemberInfo(null);
        }
      } catch { /* ignore */ }
    }, 600);
    return () => clearTimeout(timer);
  }, [memberNumber, t]);

  const handlePasswordChange = (val) => {
    setPassword(val);
    setPwErrors(val ? checkPassword(val) : []);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!memberNumber.trim()) return;

    const pwErrs = checkPassword(password);
    if (pwErrs.length > 0) { setPwErrors(pwErrs); return; }
    if (password !== confirm) { setError(t('setPassword.error.passwordMismatch')); return; }

    setLoading(true);
    try {
      const res = await fetch('/api/player-auth-ext/setup-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ memberNumber: memberNumber.trim(), email: email.trim(), password }),
      });
      const data = await res.json();

      if (!res.ok) {
        const code = data.error;
        if (code === 'MEMBER_NOT_FOUND')  setError(t('setPassword.error.memberNotFound'));
        else if (code === 'ACCOUNT_EXISTS') setError(t('setPassword.error.accountExists'));
        else if (code === 'EMAIL_TAKEN')    setError(t('setPassword.error.emailTaken'));
        else if (code === 'EMAIL_REQUIRED') setError(t('setPassword.error.emailRequired'));
        else setError(t('setPassword.error.setupFailed'));
        return;
      }

      if (data.needsLogin) {
        // Account created but auto-login failed; redirect to login
        navigate('/login', { replace: true });
        return;
      }

      // Hydrate auth context from the new BA session cookie
      await rehydratePlayer();
    } catch {
      setError(t('setPassword.error.setupFailed'));
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h1 className="text-3xl font-bold text-gray-900 text-center mb-2">
          {t('setPassword.heading.title')}
        </h1>
        <p className="text-gray-600 text-center text-sm">
          {t('setPassword.heading.subtitle')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-xl rounded-lg sm:px-10">
          {error && <Alert variant="error" className="mb-4 text-sm">{error}</Alert>}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Member number */}
            <div>
              <label htmlFor="memberNumber" className="block text-sm font-medium text-gray-700">
                {t('login.input.label.memberNumber')}
              </label>
              <input
                id="memberNumber"
                type="text"
                value={memberNumber}
                onChange={(e) => setMemberNumber(e.target.value)}
                className={inputClass}
                placeholder={t('login.input.placeholder.memberNumber')}
                required
                disabled={loading}
              />
              {memberInfo && (
                <p className="mt-1 text-xs text-green-600">✓ {memberInfo.name}</p>
              )}
            </div>

            {/* Email (needed for password reset later) */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('setPassword.input.label.email')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
                placeholder={t('setPassword.input.placeholder.email')}
                disabled={loading}
              />
              <p className="mt-1 text-xs text-gray-500">{t('setPassword.text.emailInfo')}</p>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {t('setPassword.input.label.password')}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                className={inputClass}
                placeholder={t('setPassword.input.placeholder.password')}
                required
                disabled={loading}
              />
              {/* Strength indicator */}
              {password && (
                <ul className="mt-1 space-y-0.5">
                  {['Mindst 8 tegn', 'Stort bogstav', 'Lille bogstav', 'Et tal', 'Specialtegn'].map((req) => (
                    <li
                      key={req}
                      className={`text-xs flex items-center gap-1 ${pwErrors.includes(req) ? 'text-red-500' : 'text-green-600'}`}
                    >
                      {pwErrors.includes(req) ? '✗' : '✓'} {req}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Confirm password */}
            <div>
              <label htmlFor="confirm" className="block text-sm font-medium text-gray-700">
                {t('setPassword.input.label.confirmPassword')}
              </label>
              <input
                id="confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className={`${inputClass} ${confirm && confirm !== password ? 'border-red-300' : ''}`}
                placeholder={t('setPassword.input.placeholder.confirmPassword')}
                required
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading || pwErrors.length > 0}
              className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? t('setPassword.button.setting') : t('setPassword.button.setPassword')}
            </button>
          </form>

          <div className="mt-4 text-center text-sm">
            <Link to="/login" className="text-indigo-600 hover:text-indigo-500">
              {t('resetPassword.button.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
